import type {
  CampaignContactFieldDiff,
  CampaignContactScanKind,
  CampaignContactScanResult,
  CampaignContactScanRow,
  CampaignCsv,
} from "@/types/campaign";

/** Header text reduced to letters and digits, so "First Name" and "first_name" agree. */
export const normalizeHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Which CSV column feeds which scan field. The uploaded file's headers are the
 * user's own — the import endpoint takes them verbatim — but the scan speaks a
 * fixed contact shape, so the common spellings of each column are listed here
 * and matched on the normalised header. First alias present in the file wins.
 */
export type CsvField = keyof Omit<CampaignContactScanRow, "row_number">;

export const FIELD_ALIASES: Record<CsvField, string[]> = {
  forename: ["forename", "firstname", "givenname", "name"],
  surname: ["surname", "lastname", "familyname"],
  phone_number: ["phonenumber", "phone", "mobile", "mobilenumber", "telephone", "contactnumber", "msisdn"],
  email: ["email", "emailaddress"],
  street: ["street", "streetname", "road", "addressline1"],
  name_number: ["namenumber", "housenumber", "housename", "buildingnumber", "buildingname", "number"],
  sub_dwelling: ["subdwelling", "flat", "flatnumber", "apartment", "unit", "addressline2"],
  locality: ["locality", "district", "area"],
  town: ["town", "city", "posttown"],
  county: ["county", "state", "region"],
  postcode: ["postcode", "postalcode", "zip", "zipcode"],
  country_code: ["countrycode", "country"],
  label: ["label", "labels", "tag", "tags"],
};

/**
 * The header the import endpoint knows each column by — what the template's
 * header row carries, and what an "unknown column" message should point the
 * user back to. The aliases above are what an uploaded file is matched on;
 * these are what it is told to use.
 */
export const CANONICAL_HEADERS: Record<CsvField, string> = {
  forename: "FORENAME",
  surname: "SURNAME",
  phone_number: "PHONE_NUMBER",
  email: "EMAIL",
  street: "STREET",
  name_number: "NAME_NUMBER",
  sub_dwelling: "SUB_DWELLING",
  locality: "LOCALITY",
  town: "TOWN",
  county: "COUNTY",
  postcode: "POSTCODE",
  country_code: "COUNTRY_CODE",
  label: "LABELS",
};

/** Every alias any column may be spelled as, for a fast membership test. */
const KNOWN_ALIASES = new Set(Object.values(FIELD_ALIASES).flat());

/**
 * Whether the import endpoint will recognise a column. It answers an unknown
 * one with `UNKNOWN_HEADER` and fails the whole import, so the check is worth
 * making before the file is ever sent.
 */
export const isKnownHeader = (header: string) => KNOWN_ALIASES.has(normalizeHeader(header));

/**
 * Restates the parsed CSV as scan rows. A column the file does not carry — and
 * a cell left blank — becomes null: the scan compares against stored contacts,
 * and an empty string would read as "clear this field" rather than "no value
 * supplied here".
 */
export function toContactScanRows(csv: CampaignCsv): CampaignContactScanRow[] {
  const headerByAlias = new Map<string, string>();
  for (const header of csv.headers) {
    const key = normalizeHeader(header);
    // First occurrence wins, so a duplicated column cannot shadow the original.
    if (key && !headerByAlias.has(key)) headerByAlias.set(key, header);
  }

  const columnFor = (aliases: string[]) => {
    for (const alias of aliases) {
      const header = headerByAlias.get(alias);
      if (header) return header;
    }
    return null;
  };

  const columns = Object.fromEntries(
    Object.entries(FIELD_ALIASES).map(([field, aliases]) => [field, columnFor(aliases)]),
  ) as Record<keyof Omit<CampaignContactScanRow, "row_number">, string | null>;

  return csv.rows.map((row) => {
    const read = (field: keyof typeof columns) => {
      const column = columns[field];
      if (!column) return null;
      const value = (row.values[column] ?? "").trim();
      return value ? value : null;
    };

    return {
      row_number: row.row_number,
      forename: read("forename"),
      surname: read("surname"),
      phone_number: read("phone_number"),
      email: read("email"),
      street: read("street"),
      name_number: read("name_number"),
      sub_dwelling: read("sub_dwelling"),
      locality: read("locality"),
      town: read("town"),
      county: read("county"),
      postcode: read("postcode"),
      country_code: read("country_code"),
      label: read("label"),
    };
  });
}

type Raw = Record<string, unknown>;

const isObject = (value: unknown): value is Raw => !!value && typeof value === "object";

/** First key present on the object with a usable value, as a trimmed string. */
function pickString(source: Raw, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (value == null) continue;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }
    if (typeof value === "number" || typeof value === "boolean") return String(value);
  }
  return null;
}

function pickArray(source: Raw, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

const LABEL_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Compare CSV label text to Atlas names: "spring-rentals-2026" vs "Spring Rentals 2026". */
export function labelNameKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function rememberLabelToken(ids: Set<string>, names: Set<string>, value: unknown): void {
  if (value == null) return;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    for (const part of String(value).split(/[,;]/)) {
      const token = part.trim();
      if (!token) continue;
      if (LABEL_UUID.test(token)) ids.add(token);
      else names.add(token);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) rememberLabelToken(ids, names, item);
    return;
  }
  if (!isObject(value)) return;
  const id = pickString(value, ["id", "label_id", "labelId"]);
  if (id) ids.add(id);
  rememberLabelToken(
    ids,
    names,
    pickString(value, ["name", "label", "label_name", "labelName"]),
  );
}

const LABEL_VALUE_KEYS = [
  "label_id",
  "labelId",
  "label_ids",
  "labelIds",
  "label",
  "labels",
  "resolved_labels",
  "resolvedLabels",
];

function labelRefsFrom(source: Raw | null): { ids: string[]; names: string[] } {
  const ids = new Set<string>();
  const names = new Set<string>();
  if (!source) return { ids: [], names: [] };
  for (const key of LABEL_VALUE_KEYS) {
    if (key in source) rememberLabelToken(ids, names, source[key]);
  }
  return { ids: [...ids], names: [...names] };
}

/** Fields shown first when listing a new contact's CSV values. */
const INCOMING_FIELD_ORDER = [
  "forename",
  "surname",
  "name",
  "phone_number",
  "phone_e164",
  "email",
  "street",
  "name_number",
  "sub_dwelling",
  "locality",
  "town",
  "county",
  "postcode",
  "country_code",
  "branch",
  "label",
];

function personName(source: Raw | null): string | null {
  if (!source) return null;
  const full = pickString(source, ["display_name", "displayName", "name", "full_name", "fullName"]);
  if (full) return full;
  const joined = [
    pickString(source, ["forename", "first_name", "firstName"]),
    pickString(source, ["surname", "last_name", "lastName"]),
  ]
    .filter(Boolean)
    .join(" ");
  return joined || null;
}

function incomingFields(source: Raw | null): CampaignContactFieldDiff[] {
  if (!source) return [];
  const keys = [
    ...INCOMING_FIELD_ORDER.filter((key) => key in source),
    ...Object.keys(source).filter((key) => !INCOMING_FIELD_ORDER.includes(key)),
  ];
  const seen = new Set<string>();
  const fields: CampaignContactFieldDiff[] = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    const value = source[key];
    if (value != null && typeof value === "object") continue;
    const incoming_value = pickString(source, [key]);
    if (!incoming_value) continue;
    fields.push({ field: key, existing_value: null, incoming_value });
  }
  return fields;
}

function scanKind(
  action: string | null,
  differences: CampaignContactFieldDiff[],
  contactId: string | null,
): CampaignContactScanKind {
  const status = (action ?? "").trim().toUpperCase();
  if (status === "NEW" || status === "CREATE") return "new";
  if (status === "UNCHANGED" || status === "IDENTICAL" || status === "NONE" || status === "SKIP") {
    return "unchanged";
  }
  if (status === "UPDATE" || status === "EXISTING" || status === "CONFLICT") {
    return differences.length > 0 ? "update" : "unchanged";
  }
  if (differences.length > 0) return "update";
  if (contactId) return "unchanged";
  return "new";
}

function toDiff(raw: unknown): CampaignContactFieldDiff | null {
  if (!isObject(raw)) return null;
  const field = pickString(raw, ["field", "field_name", "fieldName", "name", "key"]);
  if (!field) return null;
  return {
    field,
    existing_value: pickString(raw, [
      "existing_value",
      "existingValue",
      "existing",
      "current_value",
      "currentValue",
      "current",
      "old_value",
      "oldValue",
      "stored_value",
      "storedValue",
    ]),
    incoming_value: pickString(raw, [
      "incoming_value",
      "incomingValue",
      "incoming",
      "new_value",
      "newValue",
      "csv_value",
      "csvValue",
      "uploaded_value",
      "uploadedValue",
      "value",
    ]),
  };
}

/**
 * Reads the scan response into {@link CampaignContactScanResult}s.
 *
 * Deliberately forgiving about where the rows sit and how each difference is
 * spelled: the endpoint is new and the UI should degrade to "nothing differs"
 * rather than throwing if the envelope shifts. A row whose `row_number` cannot
 * be read is dropped — without it there is nothing to select or import.
 */
export function normalizeScanResults(raw: unknown): CampaignContactScanResult[] {
  const container = Array.isArray(raw) ? raw : isObject(raw) ? pickArray(raw, ["results", "rows", "contacts", "data", "items"]) : [];

  const results: CampaignContactScanResult[] = [];
  for (const entry of container) {
    if (!isObject(entry)) continue;
    const rowNumberRaw = entry.row_number ?? entry.rowNumber ?? entry.row;
    const rowNumber = Number(rowNumberRaw);
    if (!Number.isFinite(rowNumber)) continue;

    const differences = pickArray(entry, [
      "differences",
      "diffs",
      "changes",
      "conflicts",
      "changed_fields",
      "changedFields",
      "fields",
    ])
      .map(toDiff)
      .filter((diff): diff is CampaignContactFieldDiff => diff !== null);

    const incoming = isObject(entry.incoming) ? entry.incoming : isObject(entry.csv) ? entry.csv : null;
    const current = isObject(entry.current) ? entry.current : isObject(entry.existing) ? entry.existing : null;
    const action = pickString(entry, ["status", "match_status", "matchStatus", "result", "action"]);
    const contactId = pickString(entry, ["contact_id", "contactId"]);
    const kind = scanKind(action, differences, contactId);
    const incoming_fields = incomingFields(incoming);
    const displayName =
      personName(incoming) ||
      personName(current) ||
      personName(entry) ||
      null;
    const labelRefs = labelRefsFrom(entry);
    const incomingRefs = labelRefsFrom(incoming);
    const labelIds = [...new Set([...labelRefs.ids, ...incomingRefs.ids])];
    const labelNames = [...new Set([...labelRefs.names, ...incomingRefs.names])];
    const labelFromFields = incoming_fields.find((field) =>
      /^(label|labels|tag|tags)$/i.test(field.field),
    )?.incoming_value;

    results.push({
      row_number: rowNumber,
      status: action,
      kind,
      contact_id: contactId,
      display_name: displayName,
      branch:
        pickString(entry, ["branch", "branch_name", "branchName"]) ??
        (incoming ? pickString(incoming, ["branch", "branch_name", "branchName"]) : null),
      label: labelNames.join(", ") || labelFromFields || null,
      label_id: labelIds[0] ?? null,
      label_ids: labelIds,
      differences,
      incoming_fields,
    });
  }
  return results;
}

/**
 * Whether the scan envelope is a failure (`status: FAIL`, or an `errors` list)
 * even when HTTP is 200. Those responses have no rows to review and must not
 * skip ahead to reachout-preview.
 */
export function readScanResponse(raw: unknown): {
  results: CampaignContactScanResult[];
  failed: boolean;
  errorMessage: string | null;
} {
  const source =
    isObject(raw) && !Array.isArray(raw) && isObject(raw.data) && !Array.isArray(raw.data)
      ? raw.data
      : raw;
  const results = normalizeScanResults(source);
  if (!isObject(source) || Array.isArray(source)) {
    return { results, failed: false, errorMessage: null };
  }
  const status = (pickString(source, ["status"]) ?? "").toUpperCase();
  const errors = pickArray(source, ["errors"]);
  const failed =
    status === "FAIL" || status === "FAILED" || status === "ERROR" || errors.length > 0;
  if (!failed) return { results, failed: false, errorMessage: null };

  const first = errors.find(isObject) ?? null;
  const errorMessage =
    (first
      ? pickString(first, ["message", "detail", "description", "code"])
      : null) ??
    pickString(source, ["message", "detail", "error"]) ??
    "Could not check the uploaded contacts against your existing ones.";
  return { results, failed: true, errorMessage };
}

/**
 * Atlas label ids for a CSV import: ids the scan already resolved, plus names
 * matched against labels already loaded in the form.
 */
export function labelIdsFromCsvImport(
  rows: CampaignContactScanRow[],
  scanResults: CampaignContactScanResult[],
  knownLabels: { id: string | number; name: string }[],
): string[] {
  const ids = new Set<string>();
  const nameKeys = new Set<string>();

  const rememberName = (value: string | null | undefined) => {
    for (const part of (value ?? "").split(/[,;]/)) {
      const token = part.trim();
      if (!token) continue;
      if (LABEL_UUID.test(token)) {
        ids.add(token);
        continue;
      }
      const key = labelNameKey(token);
      if (key) nameKeys.add(key);
    }
  };

  for (const result of scanResults) {
    for (const id of result.label_ids ?? []) {
      if (id) ids.add(id);
    }
    if (result.label_id) ids.add(result.label_id);
    rememberName(result.label);
    for (const field of result.incoming_fields) {
      if (/^(label|labels|tag|tags)$/i.test(field.field)) rememberName(field.incoming_value);
    }
  }
  for (const row of rows) rememberName(row.label);

  for (const label of knownLabels) {
    const id = String(label.id ?? "").trim();
    const key = labelNameKey(label.name ?? "");
    if (id && key && nameKeys.has(key)) ids.add(id);
  }

  return [...ids];
}

/** Unique label names from the CSV rows and the scan echo. */
export function csvLabelNames(
  rows: CampaignContactScanRow[],
  scanResults: CampaignContactScanResult[],
): string[] {
  const names = new Set<string>();
  const remember = (value: string | null | undefined) => {
    for (const part of (value ?? "").split(/[,;]/)) {
      const token = part.trim();
      if (!token || LABEL_UUID.test(token)) continue;
      names.add(token);
    }
  };
  for (const result of scanResults) {
    remember(result.label);
    for (const field of result.incoming_fields) {
      if (/^(label|labels|tag|tags)$/i.test(field.field)) remember(field.incoming_value);
    }
  }
  for (const row of rows) remember(row.label);
  return [...names];
}
